import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useGranularity } from "@/context/GranularityContext";
import {
  TrendingUp, TrendingDown, Users, Gift, Banknote, ShieldAlert, Ticket,
  AlertTriangle, ArrowUpRight, RefreshCw, Percent, Clock, Sparkles,
  MousePointerClick, Package, Wallet,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  Area, AreaChart, Bar, BarChart, Legend,
} from "recharts";
import { Link } from "react-router-dom";

const fmt = (n) => (n ?? 0).toLocaleString();

/** Parse KES 3,165 / 15.7% / 7.76x / 0h back to a number for delta math. */
function valueOf(v) {
  if (typeof v === "number") return v;
  if (!v) return 0;
  const s = String(v).replace(/[^\d.\-]/g, "");
  return Number(s) || 0;
}

export default function Dashboard() {
  const g = useGranularity();
  const [m, setM] = useState(null);
  const [prev, setPrev] = useState(null);   // comparison window (or null)
  const [budget, setBudget] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const compareQs = g.compareQuery();
      const [mx, bd, al, pv] = await Promise.all([
        api.get(`/metrics?${g.query()}`),
        api.get("/budget/today"),
        api.get("/alerts"),
        compareQs ? api.get(`/metrics?${compareQs}`) : Promise.resolve(null),
      ]);
      setM(mx.data); setBudget(bd.data); setAlerts(al.data);
      setPrev(pv ? pv.data : null);
    } finally { setLoading(false); }
  }, [g]);

  useEffect(() => { load(); }, [load]);

  if (loading || !m) return <div className="flex h-60 items-center justify-center text-[#5c665a]"><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Loading metrics…</div>;

  const h = m.headline;
  const hp = prev?.headline || null;  // may be null when compare=none
  const kpis = [
    // Migrated from legacy Analytics page — at the top of the grid
    { label: "Clicks", key: "clicks_window", value: fmt(h.clicks_window), icon: MousePointerClick, color: "#66cc33", higherIsBetter: true, hint: "Running total across all referral links (per-click timestamps are not tracked by the mock DWH)." },
    { label: "Captures", key: "captures_window", value: fmt(h.captures_window), icon: Ticket, color: "#ff3399", higherIsBetter: true },
    { label: "Orders", key: "orders_window", value: fmt(h.orders_window), icon: Package, color: "#1a1c19", higherIsBetter: true },
    { label: "Revenue (window)", key: "revenue_window_kes", value: `KES ${fmt(h.revenue_window_kes)}`, icon: ArrowUpRight, color: "#66cc33", higherIsBetter: true },
    { label: "Est. LTV", key: "ltv_kes", value: `KES ${fmt(h.ltv_kes)}`, icon: Wallet, color: "#66cc33", higherIsBetter: true },
    { label: "Pending liability", key: "pending_liability_kes", value: `KES ${fmt(h.pending_liability_kes)}`, icon: Wallet, color: "#ff3399", higherIsBetter: false },
    // Pre-existing KPIs follow
    { label: "Acquired customers", key: "acquired_customers", value: fmt(h.acquired_customers), icon: Users, color: "#66cc33", higherIsBetter: true },
    { label: "Customers / referrer", key: "customers_per_referrer", value: h.customers_per_referrer, icon: Sparkles, color: "#ff3399", higherIsBetter: true },
    { label: "Avg order value", key: "aov_kes", value: `KES ${fmt(h.aov_kes)}`, icon: ArrowUpRight, color: "#1a1c19", higherIsBetter: true },
    { label: "Return rate", key: "return_rate_pct", value: `${h.return_rate_pct}%`, icon: Percent, color: "#66cc33", higherIsBetter: true },
    { label: "K-factor (viral)", key: "k_factor", value: h.k_factor, icon: TrendingUp, color: "#ff3399", higherIsBetter: true },
    { label: "Activation rate", key: "activation_rate_pct", value: `${h.activation_rate_pct}%`, icon: Ticket, color: "#66cc33", higherIsBetter: true },
    { label: "Time to 1st order (median)", key: "time_to_first_order_hrs_median", value: `${h.time_to_first_order_hrs_median}h`, icon: Clock, color: "#5c665a", higherIsBetter: false },
    { label: "LTV : CAC", key: "ltv_cac_ratio", value: `${h.ltv_cac_ratio}x`, icon: Banknote, color: "#66cc33", higherIsBetter: true },
    { label: "Rewarded referrals", key: "rewarded", value: fmt(h.rewarded), icon: Gift, color: "#ff3399", higherIsBetter: true },
    { label: "Flagged", key: "flagged", value: h.flagged, icon: ShieldAlert, color: "#a87700", higherIsBetter: false },
    { label: "Rewards paid (total)", key: "total_paid_kes", value: `KES ${fmt(h.total_paid_kes)}`, icon: Banknote, color: "#66cc33", higherIsBetter: true },
    { label: "Est. CAC", key: "cac_kes", value: `KES ${fmt(h.cac_kes)}`, icon: ArrowUpRight, color: "#1a1c19", higherIsBetter: false },
  ];

  const compareLabel = g.compare === "yoy" ? "vs year ago" : g.compare === "prev" ? "vs previous period" : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl">Growth Dashboard</h1>
          <p className="text-sm text-[#5c665a]">Live performance of MYDAWA Refer &amp; Earn. Use the granularity bar above to re-slice every number and chart.</p>
        </div>
        {compareLabel && <span className="rounded-full bg-[#ff3399]/10 px-3 py-1 text-xs font-bold text-[#c01974]" data-testid="compare-badge">Deltas shown {compareLabel}</span>}
      </div>

      {alerts && alerts.count > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-[#ff3399]/30 bg-[#fff0f5] px-4 py-3" data-testid="dashboard-alert-banner">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-[#ff3399]" />
          <div className="flex-1 text-sm">
            <div className="font-bold text-[#c01974]">{alerts.count} alert(s) need attention</div>
            <div className="text-xs text-[#5c665a]">{alerts.alerts.map((a) => a.title).join(" · ")}</div>
          </div>
          <Link to="/admin/alerts" className="admin-btn-pink">View</Link>
        </div>
      )}

      {budget && budget.cap > 0 && (
        <div className="admin-card" data-testid="budget-widget">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">Daily reward budget</div>
              <div className="mt-1 text-lg font-extrabold">
                KES {fmt(budget.used)} / {fmt(budget.cap)}
                <span className="ml-2 text-xs font-semibold text-[#5c665a]">({budget.percent}%)</span>
              </div>
              {budget.on_hold > 0 && (
                <div className="text-xs font-semibold text-[#c01974]">{budget.on_hold} reward(s) on hold — will release tomorrow</div>
              )}
            </div>
            <div className="w-48">
              <div className="h-2 overflow-hidden rounded-full bg-[#e2e8e2]">
                <div className={`h-full rounded-full ${budget.percent > 80 ? "bg-[#ff3399]" : "bg-[#66cc33]"}`}
                     style={{ width: `${Math.min(100, budget.percent)}%` }}></div>
              </div>
              <div className="mt-1 text-right text-[10px] text-[#5c665a]">KES {fmt(budget.remaining)} remaining</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {kpis.map((k) => (
          <KpiCard key={k.label} k={k} current={h[k.key]} prev={hp ? hp[k.key] : null} />
        ))}
      </div>

      <FunnelCard h={h} />

      <div className="grid gap-4 lg:grid-cols-2 min-w-0">
        <TrendCard title="Captures & rewards" series={m.trend} keys={[
          { key: "captures", color: "#66cc33", label: "Captures" },
          { key: "rewarded", color: "#ff3399", label: "Rewarded" },
        ]} />
        <TrendCard title="Acquired customers & orders" series={m.trend} keys={[
          { key: "acquired", color: "#66cc33", label: "Acquired" },
          { key: "orders", color: "#1a1c19", label: "Orders" },
        ]} />
        <TrendCard title="AOV trend (KES)" series={m.trend} keys={[
          { key: "aov", color: "#ff3399", label: "AOV" },
        ]} kind="area" />
        <TrendCard title="Revenue vs rewards paid (KES)" series={m.trend} keys={[
          { key: "revenue", color: "#66cc33", label: "Revenue" },
          { key: "paid", color: "#ff3399", label: "Rewards paid" },
        ]} kind="bar" />
      </div>
    </div>
  );
}

function KpiCard({ k, current, prev }) {
  const cur = valueOf(current);
  const prv = prev === null || prev === undefined ? null : valueOf(prev);
  let delta = null;
  if (prv !== null && prv !== 0) {
    delta = ((cur - prv) / Math.abs(prv)) * 100;
  } else if (prv === 0 && cur > 0) {
    delta = 100; // new non-zero value
  }
  // Color polarity: higher-is-better metrics go green on up.
  let color = "text-[#5c665a]";
  let TrendIcon = TrendingUp;
  if (delta !== null) {
    const up = delta > 0;
    const good = k.higherIsBetter ? up : !up;
    color = Math.abs(delta) < 1 ? "text-[#5c665a]" : good ? "text-[#3f7c21]" : "text-[#c01974]";
    TrendIcon = up ? TrendingUp : TrendingDown;
  }
  return (
    <div className="admin-card" data-testid={`kpi-${k.label}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">{k.label}</span>
        <k.icon className="h-4 w-4" style={{ color: k.color }} />
      </div>
      <div className="mt-2 text-2xl font-extrabold text-[#1a1c19]">{k.value}</div>
      {delta !== null && (
        <div className={`mt-1 flex items-center gap-1 text-xs font-semibold ${color}`} data-testid={`kpi-delta-${k.key}`}>
          <TrendIcon className="h-3.5 w-3.5" />
          {delta > 0 ? "+" : ""}{delta.toFixed(1)}%
          <span className="text-[10px] font-normal text-[#5c665a]">prev {typeof prev === "string" ? prev : fmt(prev)}</span>
        </div>
      )}
    </div>
  );
}

function FunnelCard({ h }) {
  // Four-stage acquisition funnel. Each stage shows absolute count, conversion
  // from the PREVIOUS stage (step conversion) AND overall drop-off from the top
  // of the funnel so operators can immediately see where the biggest leak is.
  const clicks = h.clicks_window ?? 0;
  const captures = h.captures_window ?? 0;
  const orders = h.orders_window ?? 0;
  const rewarded = h.rewarded_window ?? 0;
  const stages = [
    { key: "clicks", label: "Clicks", count: clicks, color: "#9eaf9b" },
    { key: "captures", label: "Captures", count: captures, color: "#66cc33" },
    { key: "orders", label: "Orders", count: orders, color: "#1a1c19" },
    { key: "rewarded", label: "Rewarded", count: rewarded, color: "#ff3399" },
  ];
  const top = stages[0].count || 1; // avoid /0
  const totalLeak = Math.max(0, top - rewarded);
  // Only flag stages where the count actually dropped vs previous; ignore
  // stages that grew (happens in dev when test fixtures have more
  // captures than clicks). biggestLeakIdx = 0 → no real drop-off to show.
  const biggestLeakIdx = (() => {
    let maxDrop = 0, idx = 0;
    for (let i = 1; i < stages.length; i++) {
      const drop = stages[i - 1].count - stages[i].count;
      if (drop > maxDrop) { maxDrop = drop; idx = i; }
    }
    return idx;
  })();
  const hasLeak = biggestLeakIdx > 0;
  return (
    <div className="admin-card" data-testid="dashboard-funnel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c665a]">Click funnel</h3>
          <p className="text-xs text-[#5c665a]">
            Top-of-funnel <strong>{clicks.toLocaleString()}</strong> clicks →{" "}
            <strong>{rewarded.toLocaleString()}</strong> rewarded referrals.
            {hasLeak && (
              <> Biggest leak:{" "}
                <strong className="text-[#c01974]">{stages[biggestLeakIdx - 1]?.label} → {stages[biggestLeakIdx]?.label}</strong>.
              </>
            )}
          </p>
        </div>
        <span className="rounded-full bg-[#fff0f5] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#c01974]">
          −{totalLeak.toLocaleString()} total drop-off
        </span>
      </div>
      <div className="mt-4 space-y-2">
        {stages.map((s, i) => {
          const pctOfTop = top > 0 ? (s.count / top) * 100 : 0;
          const prev = i === 0 ? null : stages[i - 1];
          const stepConv = prev && prev.count > 0
            ? Math.round((s.count / prev.count) * 100)
            : null;
          const stepDrop = prev ? prev.count - s.count : 0;
          const isBiggest = hasLeak && i === biggestLeakIdx;
          return (
            <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 font-bold text-[#1a1c19]">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                  {isBiggest && (
                    <span className="rounded-full bg-[#fff0f5] px-1.5 py-0 text-[9px] font-bold text-[#c01974]">
                      ↓ biggest leak
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 font-mono">
                  <span className="text-[#1a1c19]">{s.count.toLocaleString()}</span>
                  {stepConv !== null && (
                    <span className="text-[#5c665a]" data-testid={`funnel-step-conv-${s.key}`}>
                      {stepConv > 100
                        ? `n/a — backfilled data`
                        : `${stepConv}% from ${prev.label}`}
                    </span>
                  )}
                  {stepDrop > 0 && (
                    <span className="text-[#c01974]">−{stepDrop.toLocaleString()}</span>
                  )}
                </div>
              </div>
              <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-[#f0f3f0]">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, pctOfTop)}%`, backgroundColor: s.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
      {top === 1 && clicks === 0 && (
        <p className="mt-3 text-center text-[11px] text-[#5c665a]">
          No clicks in this window — send some invites to populate the funnel.
        </p>
      )}
    </div>
  );
}

function TrendCard({ title, series, keys, kind = "line" }) {
  const data = series.map((d) => ({ ...d }));
  const Chart = kind === "area" ? AreaChart : kind === "bar" ? BarChart : LineChart;
  // Measure the wrapper div ourselves — only mount the chart once we have a
  // positive width. Eliminates the Recharts "width(-1)/height(-1)" dev warning
  // that fired during the transient 0-width first paint of a flex/grid child.
  const wrapRef = React.useRef(null);
  const [size, setSize] = React.useState({ w: 0, h: 240 });
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) setSize({ w: Math.floor(rect.width), h: 240 });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="admin-card min-w-0">
      <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-[#5c665a]">{title}</h3>
      {data.length === 0 ? (
        <div className="flex h-60 items-center justify-center text-xs text-[#5c665a]">
          No data in this window.
        </div>
      ) : (
        <div ref={wrapRef} style={{ width: "100%", height: 240 }}>
          {size.w > 0 && (
            <Chart data={data} width={size.w} height={size.h} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8e2" />
            <XAxis dataKey="bucket" fontSize={10} tick={{ fill: "#5c665a" }} />
            <YAxis fontSize={10} tick={{ fill: "#5c665a" }} />
            <Tooltip />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            {keys.map((k) => {
              if (kind === "area") return <Area key={k.key} type="monotone" dataKey={k.key} name={k.label} stroke={k.color} fill={k.color} fillOpacity={0.18} strokeWidth={2.5} />;
              if (kind === "bar") return <Bar key={k.key} dataKey={k.key} name={k.label} fill={k.color} radius={[4, 4, 0, 0]} />;
              return <Line key={k.key} type="monotone" dataKey={k.key} name={k.label} stroke={k.color} strokeWidth={2.5} dot={false} />;
            })}
          </Chart>
          )}
        </div>
      )}
    </div>
  );
}
